<template>
  <div>
    <h4 class="mb-8 mt-4 text-left text-dark-gray sm:mt-2">
      Let's start with the basics.
    </h4>

    <div v-if="!!data?.identity?.id">
      <div class="flex flex-wrap items-start">
        <div class="mb-5 w-full flex-col">
          <p class="categoryParagraph">Legal Name</p>
          <p class="text-dark-gray">
            {{ capitalizeEachWord(data.identity.legal_name) }}
          </p>
        </div>
        <div class="mb-5 w-full flex-col">
          <p class="categoryParagraph">Identifiers</p>
          <p class="text-dark-gray">
            {{ findDotAndMcValues }}
          </p>
        </div>
        <div class="mb-5 w-full flex-col">
          <p class="categoryParagraph">Authority</p>
          <p class="text-dark-gray">
            {{ findAuthorityValues == !'' ? findAuthorityValues : '-' }}
          </p>
        </div>
      </div>

      <p class="mb-4 border-b border-light-gray text-left text-gray-over-dark">
        Physical Address
      </p>
      <div class="flex flex-wrap items-start">
        <div
          v-for="(item, index) in items?.physicalAddress"
          :key="index"
          class="mb-5 flex-col"
          :class="
            index === 0 || index === 1 ? 'w-1/2 md:mr-8 md:w-max' : 'mr-6 w-max'
          "
        >
          <p class="categoryParagraph">
            {{ item?.name }}
          </p>
          <p
            class="text-dark-gray"
            :class="[index == 4 ? 'uppercase' : '']"
          >
            {{
              capitalizeEachWord(
                data.identity?.physical_address?.[item?.value]
              ) || '-'
            }}
          </p>
        </div>
      </div>

      <div
        v-if="
          data.identity?.mailing_address?.street1 !==
          data.identity?.physical_address?.street1
        "
      >
        <p
          class="mb-4 border-b border-light-gray text-left text-gray-over-dark"
        >
          Mailing Address
        </p>
        <div class="flex flex-wrap items-start">
          <div
            v-for="(item, index) in items?.mailingAddress"
            :key="index"
            class="mb-5 flex-col"
            :class="
              index === 0 || index === 1
                ? 'w-1/2 md:mr-8 md:w-max'
                : 'mr-6 w-max'
            "
          >
            <p class="categoryParagraph">
              {{ item?.name }}
            </p>
            <p class="text-dark-gray">
              {{
                capitalizeEachWord(
                  data.identity?.mailing_address?.[item?.value]
                ) || '-'
              }}
            </p>
          </div>
        </div>
      </div>

      <p class="mb-4 border-b border-light-gray text-left text-gray-over-dark">
        FMCSA Contact Information
      </p>
      <div class="flex flex-wrap items-start">
        <div class="mb-5 mr-6 flex-col">
          <p class="categoryParagraph">Phone(s)</p>
          <template v-if="data.identity?.phones?.length > 0">
            <template
              v-for="(phone, index) in data.identity?.phones"
              :key="index"
            >
              <p
                v-if="phone?.is_type?.includes('census')"
                class="mt-1 text-dark-gray"
              >
                {{ formatPhone(phone?.value) || '&nbsp;' }}
              </p>
            </template>
          </template>
          <template v-else>
            <p class="mt-1 text-dark-gray">-</p>
          </template>
        </div>
        <div class="mb-5 flex-col">
          <p class="categoryParagraph">Email(s)</p>
          <template v-if="data.identity?.email_addresses?.length > 0">
            <template
              v-for="(email, index) in data.identity?.email_addresses"
              :key="index"
            >
              <p
                v-if="email?.is_type.includes('census')"
                class="mt-1 text-dark-gray"
              >
                {{ email?.value || '&nbsp;' }}
              </p>
            </template>
          </template>
          <template v-else>
            <p class="mt-1 text-dark-gray">-</p>
          </template>
        </div>
      </div>
    </div>
    <form-navigation-buttons
      v-if="!isNavigationDisabled"
      :enabled="!isNavigationDisabled"
      :component-identifier="componentIdentifier"
      :buttons-to-display="buttonsToDisplay"
      :is-button-disabled="isButtonDisabled"
      :hide-back-button="hideBackButton"
      @next="handleNext"
      @back="handleBack"
      @exit-modal="emitExitModal"
    />
  </div>
</template>

<script>
import FormNavigationButtons from '@/components/FormNavigationButtons.vue';
import { useFormRouter } from '@/composables/formRouter';
import stepInformation from '@/common/stepInformation';
import { capitalize, formatPhoneParenthesis } from '@/common/validators';
import { useStore } from 'vuex';
import { computed } from 'vue';

export default {
  name: 'IdentityStepOne',
  components: {
    FormNavigationButtons,
  },
  props: {
    data: Object,
    formPosition: Number,
    title: String,
    updateField: Function,
    nextStep: Function,
    stepName: String,
    skipStep: Function,
    screenSize: String,
  },
  setup(_, { emit }) {
    const store = useStore();
    const componentIdentifier = 'Identity-1';
    const formRouter = useFormRouter(componentIdentifier);
    const isNavigationDisabled = computed(() => formRouter.isDisabled.value);
    const firstTime = computed(
      () =>
        store.state.firstTime?.[
          formRouter.componentStep.value?.firstTimeIdentifier
        ]
    );
    const buttonsToDisplay = 'report-error';
    const isButtonDisabled = false;

    const handleNext = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleNext' called"
      );
      if (isNavigationDisabled.value)
        return store.commit(
          'addFormDebugMessage',
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToNextStep();
    };

    const handleBack = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'handleBack' called"
      );
      if (isNavigationDisabled.value)
        return console.log(
          'ERROR: ' +
            componentIdentifier +
            ': formRouter is disabled, returning'
        );
      formRouter.moveToPreviousStep();
    };

    const hideBackButton = computed(() => {
      if (
        store.state.carriers
          ?.availableCarrierElectedClassificationsHaveLoaded === false
      )
        return true;
      return !(
        store.state.carriers?.availableCarrierElectedClassifications?.length > 0
      );
    });

    const emitExitModal = () => {
      store.commit(
        'addFormDebugMessage',
        componentIdentifier + ": 'emitExitModal' called"
      );
      emit('exit-modal');
    };

    return {
      componentIdentifier,
      handleNext,
      handleBack,
      buttonsToDisplay,
      isButtonDisabled,
      isNavigationDisabled,
      firstTime,
      hideBackButton,
      emitExitModal,
    };
  },
  data() {
    return {
      items: [],
    };
  },
  computed: {
    findDotAndMcValues() {
      const mainIdentifiers = ['DOT', 'MC'];
      const identifiers =
        this.data.identity?.identifiers?.filter(
          (el) => mainIdentifiers.includes(el.is_type) || el.is_manually_created
        ) ?? [];
      const identifierStrings = identifiers.map(
        (id) => `${this.formatIsType(id.is_type)} ${id.value}`
      );
      return identifierStrings.join(' ');
    },
    findAuthorityValues() {
      const authorityArr = Object.keys(this.data.identity?.authorities);
      const findActiveAuthorities = authorityArr.filter(
        (el) => this.data.identity?.authorities[el] === 'Active'
      );

      const separateActiveAuthorities = findActiveAuthorities.reduce(
        (tot, el, index, arr) => {
          return (tot +=
            el.split('_')[0] + (arr.length - 1 !== index ? ', ' : ''));
        },
        ' '
      );

      return capitalize(separateActiveAuthorities);
    },
  },
  mounted() {
    this.items = stepInformation.identity;
  },
  methods: {
    capitalizeEachWord(string) {
      if (string?.length == 2) {
        return string.toUpperCase();
      } else {
        return string && capitalize(string);
      }
    },
    formatPhone(phone) {
      return formatPhoneParenthesis(phone);
    },
    formatIsType(type) {
      if (!type) return '';

      return type?.replace('_', ' ');
    },
  },
};
</script>
<style scoped>
p {
  @apply text-left;
}
.categoryParagraph {
  @apply mb-1 text-xxs font-bold uppercase text-gray-over-dark;
}
</style>
